import { useSyncExternalStore } from 'react';

/**
 * App instalável (PWA): registra o service worker e guarda o pedido de instalação do Chrome para o
 * item "Instalar o app" do menu (o Chrome só oferece quando o app ainda não está instalado).
 */

interface InstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let deferred: InstallPromptEvent | null = null;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((l) => l());

export function setupPwa(): void {
  if (typeof window === 'undefined') return;
  // Service worker só em https ou localhost (como o microfone).
  if ('serviceWorker' in navigator && window.isSecureContext) {
    window.addEventListener('load', () => void navigator.serviceWorker.register('/sw.js').catch(() => undefined));
  }
  window.addEventListener('beforeinstallprompt', (e) => {
    // Sem a faixa automática do Chrome: a instalação fica no menu do usuário.
    e.preventDefault();
    deferred = e as InstallPromptEvent;
    notify();
  });
  window.addEventListener('appinstalled', () => {
    deferred = null;
    notify();
  });
}

/** Aberto como app instalado (sem a barra do navegador). */
export const isStandalone = () => typeof window !== 'undefined' && window.matchMedia('(display-mode: standalone)').matches;

export function useInstallPrompt(): { canInstall: boolean; install: () => Promise<void> } {
  const canInstall = useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => deferred !== null,
  );
  return {
    canInstall,
    install: async () => {
      const e = deferred;
      if (!e) return;
      await e.prompt();
      await e.userChoice.catch(() => undefined);
      deferred = null;
      notify();
    },
  };
}
